// src/pages/Builders/ProjectDetail.jsx
import React, { useState, useEffect } from "react";
import Layout from "../../layout/builders/Layout";
import MasterDataDisplay from "../../layout/common/MasterDataDisplay";
import PaymentValue from "../../layout/builders/Payment/PaymentValue";
import PaymentRequest from "../../layout/builders/Payment/PaymentRequest";
import ProjectPaymentHistory from "../../layout/builders/Payment/ProjectPaymentHistory";
import BuilderProjectValueSummary from "../../layout/builders/Payment/BuilderProjectValueSummary";
import DocumentUpload from "../../layout/builders/Documentupload..jsx";
import WeeklyUpdateInchargePanel from "../../layout/builders/ERP/WeeklyUpdateInchargePanel";
import { AttendanceSubTab, PettyCashSubTab, MaterialsSubTab } from "../../layout/builders/ERP/ERPTrackingTab";
import {
  ArrowLeft, Database, FileText, History,
  Users, Wallet, Package, CalendarDays
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import useAuthStore from "../../store/useAuthStore";
import { axiosInstancev1 } from "../../lib/axiosInstance";

const ProjectDetail = () => {
  const { user } = useAuthStore();
  const isIncharge = user?.role === "incharge";
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "master-data");
  const [paymentSubTab, setPaymentSubTab] = useState(searchParams.get("subtab") || "payment");

  // Sync state with URL params
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    const urlSubTab = searchParams.get("subtab");
    if (urlTab && urlTab !== activeTab) setActiveTab(urlTab);
    if (urlTab === "finances" && urlSubTab && urlSubTab !== paymentSubTab) setPaymentSubTab(urlSubTab);
  }, [searchParams]);

  const handleGoBack = () => navigate(-1);

  // Incharge: only assigned projects (all sidebar tabs use this project id)
  useEffect(() => {
    if (!isIncharge || !projectId) return;

    const verifyAccess = async () => {
      try {
        const builderId = user?.builderId || user?.builder_id || user?.id;
        const res = await axiosInstancev1.get(
          `/projects?status=active&builderId=${builderId}&limit=200`
        );
        const allowed = (res.data?.projects || []).map((p) => Number(p.id));
        const pid = Number(projectId);

        if (allowed.length === 0) {
          toast.error("No projects assigned to your account. Ask your builder to assign projects.");
          navigate("/builder/projects", { replace: true });
          return;
        }

        if (!allowed.includes(pid)) {
          const fallback = allowed[0];
          toast.error("This project is not assigned to you. Opening an assigned project.");
          sessionStorage.setItem("incharge_active_project_id", String(fallback));
          const tab = searchParams.get("tab") || "master-data";
          navigate(`/builder/project/${fallback}/detail?tab=${tab}`, { replace: true });
          return;
        }

        sessionStorage.setItem("incharge_active_project_id", String(projectId));
      } catch {
        sessionStorage.setItem("incharge_active_project_id", String(projectId));
      }
    };

    verifyAccess();
  }, [isIncharge, projectId, user, navigate, searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "finances") {
      const currentSubTab = searchParams.get("subtab") || "payment";
      setPaymentSubTab(currentSubTab);
      setSearchParams({ tab: tabId, subtab: currentSubTab });
    } else {
      setSearchParams({ tab: tabId });
    }
  };

  const handlePaymentSubTabChange = (subTabId) => {
    setPaymentSubTab(subTabId);
    setSearchParams({ tab: "finances", subtab: subTabId });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "master-data":
        return <MasterDataDisplay projectId={projectId} />;

      case "finances":
        return (
          <div className="space-y-3 sm:space-y-4 lg:space-y-6">
            {projectId && <BuilderProjectValueSummary projectId={projectId} />}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-2 sm:space-x-4 lg:space-x-8 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => handlePaymentSubTabChange("payment")}
                  className={`whitespace-nowrap py-2 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 flex-shrink-0 ${
                    paymentSubTab === "payment" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}>
                  <span className="w-3 h-3 sm:w-4 sm:h-4 text-sm sm:text-base lg:text-lg font-bold">₹</span>
                  Payments
                </button>
                <button
                  onClick={() => handlePaymentSubTabChange("history")}
                  className={`whitespace-nowrap py-2 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 flex-shrink-0 ${
                    paymentSubTab === "history" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}>
                  <History className="w-3 h-3 sm:w-4 sm:h-4" />
                  Payment History
                </button>
              </nav>
            </div>
            {paymentSubTab === "payment" ? (
              <>
                <div className="mb-3 sm:mb-4">
                  <h3 className="text-xs sm:text-sm lg:text-base font-semibold text-gray-700 mb-2">Total Project Value</h3>
                  <PaymentValue projectId={projectId} />
                </div>
                <div className="flex items-center my-3 sm:my-4 lg:my-6">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="mx-2 sm:mx-3 lg:mx-4 text-gray-400 font-semibold text-xs uppercase">or</span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>
                <div className="mb-3 sm:mb-4">
                  <h3 className="text-xs sm:text-sm lg:text-base font-semibold text-gray-700 mb-2">Customer Payment Requests</h3>
                  <PaymentRequest projectId={projectId} />
                </div>
              </>
            ) : (
              <ProjectPaymentHistory projectId={projectId} />
            )}
          </div>
        );

      case "docs":
        return (
          <div className="space-y-4">
            {isIncharge && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="font-bold text-sm text-indigo-900">Weekly Site Update</p>
                  <p className="text-xs text-indigo-700 mt-0.5">
                    Upload interior/exterior photos and weekly progress points here
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTabChange("weekly-update")}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 shrink-0"
                >
                  <CalendarDays size={14} /> Create Weekly Update
                </button>
              </div>
            )}
            <DocumentUpload projectId={projectId} onBack={handleGoBack} />
          </div>
        );

      case "weekly-update":
        return <WeeklyUpdateInchargePanel projectId={projectId} />;

      case "attendance":
        return <AttendanceSubTab projectId={projectId} builderId={user?.id} />;

      case "pettycash":
        return <PettyCashSubTab projectId={projectId} builderId={user?.id} />;

      case "materials":
        return <MaterialsSubTab projectId={projectId} builderId={user?.id} />;

        // case "erp":
        //   return <ERPTrackingTab projectId={projectId} />;

      default:
        return <MasterDataDisplay projectId={projectId} />;
    }
  };

  const tabItems = [
    { id: "master-data", label: "Master Data", icon: <Database className="w-4 h-4" /> },
    { id: "attendance", label: "Attendance", icon: <Users className="w-4 h-4" /> },
    { id: "pettycash", label: "Petty Cash", icon: <Wallet className="w-4 h-4" /> },
    { id: "materials", label: "Materials", icon: <Package className="w-4 h-4" /> },
    { id: "docs", label: "Docs", icon: <FileText className="w-4 h-4" /> },
    { id: "weekly-update", label: "Weekly Update", icon: <CalendarDays className="w-4 h-4" /> },
    !isIncharge && { id: "finances", label: "Transactions", icon: <span className="w-4 h-4 text-lg font-bold">₹</span> },
  ].filter(Boolean);

  return (
    <Layout>
      <div className="h-full flex flex-col bg-gray-50 rounded-lg shadow-md">
        {/* Header */}
        <div className="px-2 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-3 border-b border-gray-200 bg-white rounded-t-lg flex-shrink-0 flex items-center sticky top-0 z-20 shadow-sm">
          <button
            onClick={handleGoBack}
            className="p-1 sm:p-1.5 mr-1 sm:mr-2 md:mr-3 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-full transition-colors duration-150 flex-shrink-0"
            aria-label="Go back">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h2 className="text-xs sm:text-sm md:text-base lg:text-lg font-semibold text-gray-800 flex items-center truncate">
            <span className="hidden sm:inline">Project Overview (ID: {projectId})</span>
            <span className="sm:hidden">Project {projectId}</span>
          </h2>
        </div>

        {/* Content — tab is driven by URL (?tab=) and main sidebar for incharge */}
        <div className="flex flex-1 overflow-hidden flex-col">
          {!isIncharge && (
            <nav
              className="flex flex-row gap-2 p-2 sm:p-3 border-b border-gray-200 bg-white overflow-x-auto scrollbar-hide flex-shrink-0"
              aria-label="Project tabs"
            >
              {tabItems.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`whitespace-nowrap py-2 px-3 rounded-lg font-medium text-xs sm:text-sm flex items-center gap-2 transition-colors duration-200 focus:outline-none flex-shrink-0 ${
                    activeTab === tab.id
                      ? "bg-blue-50 text-blue-700 border border-blue-100"
                      : "text-gray-600 hover:bg-gray-50 border border-transparent"
                  }`}
                >
                  <span className={activeTab === tab.id ? "text-blue-600" : "text-gray-400"}>
                    {tab.icon}
                  </span>
                  {tab.label}
                </button>
              ))}
            </nav>
          )}
          <div className="flex-grow overflow-y-auto p-2 sm:p-3 md:p-4 lg:p-6 bg-gray-50 w-full">
            {renderTabContent()}
          </div>
        </div>
      </div>
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </Layout>
  );
};

export default ProjectDetail;
